import {useQuery} from 'react-query'
import {fetchProviderInfo} from './_requests'
import {useAuth} from './Auth'

/**
 * Provider Information Interface (from API)
 */
export interface ProviderInfoResponse {
  providerCode: string
  providerName: string
  address: string
  city: string
  province: string
  country: string
  phone: string
  roleId: number
  email: string
  isActive: boolean
}

/**
 * Custom hook to fetch provider information using React Query
 *
 * Benefits:
 * - Automatic caching in memory
 * - Background refetch on mount
 * - Deduplication (multiple calls = single request)
 * - Automatic retry on failure
 * - Cache persistence across refreshes
 *
 * Usage:
 * ```tsx
 * const { data: providerInfo, isLoading, error } = useProviderInfo()
 * ```
 */
export const useProviderInfo = () => {
  const {auth} = useAuth()

  return useQuery<ProviderInfoResponse, Error>(
    ['providerInfo', auth?.token], // Query key with token for cache invalidation
    fetchProviderInfo, // Fetch function
    {
      enabled: !!auth?.token, // Only run if token exists
      staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes (300000ms)
      cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes (600000ms)
      refetchOnMount: 'always', // Always refetch on component mount
      refetchOnWindowFocus: true, // Refetch when user returns to tab
      refetchOnReconnect: true, // Refetch when internet reconnects
      retry: 2, // Retry failed requests twice
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      onError: (error) => {
        console.error('Error fetching provider info:', error)
      },
    }
  )
}

/**
 * Hook to check if provider info is being fetched
 * Useful for showing loading states
 */
export const useProviderInfoLoading = () => {
  const {isLoading, isFetching} = useProviderInfo()
  return isLoading || isFetching
}
